import { Component, Input } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { IndianCurrencyPipe } from 'src/app/pipe/indian-currency.pipe';
import { PharmacyBillResult } from 'src/app/models/pharmacy.model';
import { environment } from 'src/environments/environment';
import { FallbackImageDirective } from 'src/app/common/directive/fallback-image.directive';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { PrintSettingService } from '../../settings/hospital-setting/print-setting/print-setting.service';
import { IHospitalInfo } from '../../hospital/ihospital-info';

@Component({
  selector: 'app-pharmacy-receipt',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TranslateModule,
    IndianCurrencyPipe,
    FallbackImageDirective,
  ],
  providers: [IndianCurrencyPipe, DatePipe],
  templateUrl: './pharmacy-receipt.component.html',
})
export class PharmacyReceiptComponent {

  @Input() billResult: PharmacyBillResult;
  @Input() hospitalInfo: IHospitalInfo;
  @Input() doctorInfo: any;

  staticimagepath = environment.imagePath;

  constructor(
    private http: HttpClient,
    private hospitalState: HospitalStateService,
    private printSettingService: PrintSettingService,
    private datePipe: DatePipe,
    private indianCurrencyPipe: IndianCurrencyPipe
  ) { }

  async createPrintContent(): Promise<string> {
    try {
      let template = await firstValueFrom(
        this.http.get('assets/print-template.html', { responseType: 'text' })
      ) || '';

      const hospitalSafe = this.hospitalState.getHospital();
      const settings = await this.printSettingService.getPrintSettingPromise(hospitalSafe.id);

      // Format items table
      let tableRows = '';
      (this.billResult?.items || []).forEach((item: any, i: number) => {
        tableRows += `
          <tr>
            <td style="border: 1px solid #ccc; padding: 8px;">${i + 1}</td>
            <td style="border: 1px solid #ccc; padding: 8px;">${item.drugName}</td>
            <td style="border: 1px solid #ccc; padding: 8px;">${item.batchNo}</td>
            <td style="border: 1px solid #ccc; padding: 8px;">${this.datePipe.transform(item.expiryDate, 'MM/yyyy') || ''}</td>
            <td style="border: 1px solid #ccc; padding: 8px; text-align: right;">${item.quantity}</td>
            <td style="border: 1px solid #ccc; padding: 8px; text-align: right;">${this.indianCurrencyPipe.transform(item.unitPrice)}</td>
            <td style="border: 1px solid #ccc; padding: 8px; text-align: right;">${item.gstPercentage}%</td>
            <td style="border: 1px solid #ccc; padding: 8px; text-align: right;">${this.indianCurrencyPipe.transform(item.totalAmount)}</td>
          </tr>
        `;
      });

      const billNo = this.billResult?.billNo || '';
      const billDate = this.datePipe.transform(this.billResult?.billDate, 'dd-MMM-yyyy') || '';
      const patientName = this.billResult?.patientName || '';
      const patientMobile = this.billResult?.patientMobile || '';
      const patientAge = this.billResult?.patientAge || '';
      const patientGender = this.billResult?.patientGender === 'M' ? 'Male' : 'Female';
      const patientNo = this.billResult?.patientNumber || '';
      const patientAddress = this.billResult?.patientAddress || '';
      const doctorName = this.billResult?.doctorName || '';

      const billDetailsHeader = `
        <div style="text-align: center; margin-bottom: 10px;">
          <h2 style="margin: 0; text-transform: uppercase; text-decoration: underline; font-size: 18px;">Pharmacy Receipt</h2>
        </div>
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 12px; background-color: #f2f2f2; border: 1px solid #ccc;">
          <tr>
            <td style="padding: 5px; text-align: left; width: 33.33%;"><strong>Bill No:</strong> ${billNo}</td>
            <td style="padding: 5px; text-align: left; width: 33.33%;"><strong>Bill Date:</strong> ${billDate}</td>
            <td style="padding: 5px; text-align: left; width: 33.33%;"><strong>Patient Name:</strong> ${patientName} (${patientNo})</td>
          </tr>
          <tr>
            <td style="padding: 5px; text-align: left;"><strong>Age/Gender:</strong> ${patientAge} / ${patientGender}</td>
            <td style="padding: 5px; text-align: left;"><strong>Mobile:</strong> ${patientMobile}</td>
            <td style="padding: 5px; text-align: left;">
              ${doctorName ? `<strong>Prescribed By:</strong> ${doctorName}` : ''}
            </td>
          </tr>
          ${patientAddress ? `
          <tr>
            <td colspan="3" style="padding: 5px; text-align: left;"><strong>Address:</strong> ${patientAddress}</td>
          </tr>` : ''}
        </table>
      `;

      const sectionContent = `
        ${billDetailsHeader}
        <div class="print-table-wrapper">
          <table class="table table-bordered" style="width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 14px;">
            <thead>
              <tr style="background-color: #f2f2f2;">
                <th style="border: 1px solid #ccc; padding: 5px; text-align: left; width: 5%;">No.</th>
                <th style="border: 1px solid #ccc; padding: 5px; text-align: left; width: 25%">Product Name</th>
                <th style="border: 1px solid #ccc; padding: 5px; text-align: left; width: 11%">Batch</th>
                <th style="border: 1px solid #ccc; padding: 5px; text-align: left; width: 11%">Expiry</th>
                <th style="border: 1px solid #ccc; padding: 5px; text-align: right; width: 8%;">Qty</th>
                <th style="border: 1px solid #ccc; padding: 5px; text-align: right; width: 15%;">MRP</th>
                <th style="border: 1px solid #ccc; padding: 5px; text-align: right; width: 10%;">GST%</th>
                <th style="border: 1px solid #ccc; padding: 5px; text-align: right; width: 15%;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </div>
        <div class="summary-totals" style="text-align: right; margin-top: 70px; font-size: 14px;">
          <p style="margin: 5px 0;"><strong>Sub Total:</strong> ${this.indianCurrencyPipe.transform(this.billResult?.subTotal)}</p>
          <p style="margin: 5px 0;"><strong>Total GST:</strong> ${this.indianCurrencyPipe.transform(this.billResult?.totalGST)}</p>
          ${this.billResult?.discount ? `<p style="margin: 5px 0;"><strong>Discount:</strong> ${this.indianCurrencyPipe.transform(this.billResult.discount)}</p>` : ''}
          ${this.billResult?.roundOff ? `<p style="margin: 5px 0;"><strong>Round Off:</strong> ${this.indianCurrencyPipe.transform(this.billResult.roundOff)}</p>` : ''}
          <div style="margin-top: 10px; font-size: 18px;">
            <strong>Grand Total:</strong> ${this.indianCurrencyPipe.transform(this.billResult?.amount)}
          </div>
        </div>
      `;

      const fullImagePath = hospitalSafe.logo ? `${this.staticimagepath}${hospitalSafe.logo}` : null;
      const defaultLogo = `${window.location.origin}/assets/images/svgs/hospital.png`;

      const replacements: Record<string, string> = {
        reportTitle: 'Pharmacy Receipt',
        hospitalName: hospitalSafe.name || '',
        hospitalAddress: hospitalSafe.address || '',
        phone: hospitalSafe.phone || '',
        reportSections: sectionContent,
        doctorName: this.doctorInfo?.name || this.billResult?.doctorName || '',
        qualification: this.doctorInfo?.degree || '',
        email: this.doctorInfo?.email || hospitalSafe.email || '',
        logo: `<img src="${fullImagePath || defaultLogo}" class="logo" onerror="this.onerror=null;this.src='${defaultLogo}'"/>`,
        patientName: patientName,
        patientAge: patientAge,
        opdNumber: '', // Not used for pharmacy in the same format
        opdDate: ''
      };

      let filledTemplate = template;
      for (const key in replacements) {
        const value = replacements[key] ?? '';
        const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
        filledTemplate = filledTemplate.replace(regex, value);
      }

      // Apply settings
      if (settings) {
        const logoPos = settings.logoPosition === 'center' ? 'column' : (settings.logoPosition === 'left' ? 'row' : 'row-reverse');
        const headerClass = settings.logoPosition === 'center' ? 'center' : '';
        const headerAlignment = settings.addressAlignment || 'left';

        const styles = `
          :root {
            --logo-size: ${settings.logoSize || '60px'};
            --logo-position: ${logoPos};
            --header-alignment: ${headerAlignment};
            --hospital-name-size: ${settings.hospitalNameSize || 20}px;
            --address-font-size: ${settings.addressFontSize || 13}px;
            --footer-display: ${settings.showFooter ? 'block' : 'none'};
            --doctor-signature-top-display: ${settings.showFooter && settings.doctorNamePosition === 'top' ? 'block' : 'none'};
            --doctor-signature-bottom-display: ${settings.showFooter && settings.doctorNamePosition === 'footer' ? 'block' : 'none'};
          }
          .table-bordered th, .table-bordered td { border: 1px solid #ccc !important; }
        `;
        filledTemplate = filledTemplate.replace('</head>', `<style>${styles}</style></head>`).replace('{{headerClass}}', headerClass);
      } else {
        filledTemplate = filledTemplate.replace('{{headerClass}}', '');
      }

      return filledTemplate;
    } catch (error) {
      console.error('Error creating pharmacy receipt content:', error);
      throw error;
    }
  }
}
